<template>
  <BittsLoading
    :is-loading="!ready"
    :with-transition="false"
    class="sharing-settings-by-partners-tab min-h-[50vh]"
  >
    <div class="sharing-settings-by-partners-tab__actions">
      <PartnerTabActions
        v-if="rows.length"
        :partner-options="partnerOptions"
        :owner-options="teamOwnerOptions"
        :tag-options="tagOptions"
        @search-applied="applySearch"
        @filters-applied="applyFilters"
        @population-filters-applied="applyPopulationFilters"
      >
        <template #segment>
          <slot name="prepend" />
        </template>
        <template #actions>
          <slot name="actions" />
        </template>
      </PartnerTabActions>
      <!-- With no partners there is no filter bar, so the row the toolbar
           normally provides has to be kept here. -->
      <div v-else class="flex items-center justify-between gap-16">
        <slot name="prepend" />
        <slot name="actions" />
      </div>
    </div>
    <BittsTablePro
      ref="table"
      :columns="columnDefinitions"
      :default-column-def="defaultColumnDefinition"
      :rows="filteredRows"
      :row-height="52"
      row-id="id"
      :pagination="false"
      dom-layout="normal"
      :suppress-movable-columns="true"
      :no-rows="!filteredRows.length && ready"
      :no-rows-props="noRowsProps"
      :passed-context="gridContext"
      :row-selection="rowSelection"
      class="sharing-settings-by-partners-tab__table"
      :style="{ height: tableHeight }"
      @grid-ready="handleGridReady"
    />
    <PartnershipSettingsModal
      v-if="partnerSettingsTarget"
      :partner-id="partnerSettingsTarget.partnerId"
      :initial-tab="partnerSettingsTarget.tab"
      :initial-segment="partnerSettingsTarget.segment"
      @close="closePartnerSettings"
    />
  </BittsLoading>
</template>

<script setup lang="ts">
import { type BittsIcon, BittsLoading, BittsTablePro } from '@crossbeam/bitts';

import type { RowSelectionOptions } from '@ag-grid-community/core';
import { keyBy } from 'lodash';
import { computed, onMounted, ref, shallowRef, useTemplateRef } from 'vue';
import { useRouter } from 'vue-router';

import { PartnershipSettingsTabType } from '@/components/partners/DataSharingSettings/SettingsModal/constants';
import PartnershipSettingsModal from '@/components/partners/DataSharingSettings/SettingsModal/PartnershipSettingsModal.vue';

import { crossbeamApi } from '@/api';
import { useViewportFillHeight } from '@/composables/useViewportFillHeight';
import { captureException } from '@/errors';
import { useTeamStore } from '@/stores';
import { useDataSharesStore } from '@/stores/DataSharesStore';
import { usePartnersStore } from '@/stores/PartnersStore';
import { usePopulationsStore } from '@/stores/PopulationsStore';
import type { Partner } from '@/types/partners';
import type { SegmentType } from '@/types/populations';
import { buildOwnerOptions } from '@/utils/ownerOptions';

import PartnerTabActions from './components/PartnerTabActions.vue';
import {
  defaultColumnDefinition,
  usePartnerTabColumnDefinitions,
} from './composables/usePartnerTabColumnDefinitions';
import usePartnerTabFilters from './composables/usePartnerTabFilters';
import usePartnerTabSelection from './composables/usePartnerTabSelection';
import { buildPartnerRows } from './partnerRows';
import type { PartnerActivityType, SharingSideType } from './types';

const { canManagePartners = false } = defineProps<{
  canManagePartners?: boolean;
}>();

/** Hoisted so a re-render cannot hand BittsTablePro a fresh object and churn an AG Grid option. */
const NO_PARTNERS_PROPS = {
  title: 'No partners found',
  description:
    "You don't have any active partners yet. Invite partners to start sharing data.",
  icon: ['fak', 'partner'] as BittsIcon,
};

const NO_MATCHES_PROPS = {
  title: 'No partners match your filters',
  description: 'Try removing a filter or searching for a different partner.',
  icon: ['far', 'search'] as BittsIcon,
};

const partnersStore = usePartnersStore();
const populationsStore = usePopulationsStore();
const dataSharesStore = useDataSharesStore();
const teamStore = useTeamStore();
const router = useRouter();

const columnDefinitions = usePartnerTabColumnDefinitions();

const table = useTemplateRef<InstanceType<typeof BittsTablePro>>('table');
const tableHeight = useViewportFillHeight(() => table.value?.$el);

const ready = computed(
  () => partnersStore.ready && populationsStore.ready && dataSharesStore.ready,
);

const partnerActivityLookup = shallowRef<Record<string, PartnerActivityType>>(
  {},
);

/**
 * Not awaited by `ready`: the rows render without it and gain their Last
 * Activity and Last Sync dates once it resolves.
 */
async function loadPartnerActivity() {
  const { data, error } = await crossbeamApi.GET(
    '/v0.1/partners/global/activity',
  );
  if (error) {
    captureException(error);
    return;
  }
  if (!data) return;
  partnerActivityLookup.value = keyBy(data.items, 'partner_organization_uuid');
}

onMounted(loadPartnerActivity);

const rows = computed(() => {
  if (!ready.value) return [];

  return buildPartnerRows(partnersStore.partnerOrgs, {
    populations: populationsStore.populations,
    partnerPopulations: populationsStore.partnerPopulations,
    greenfieldSharesLookup: dataSharesStore.greenfieldSharesLookup,
    getOutgoingVisibility: dataSharesStore.outgoingVisibilityLookup,
    getOutgoingOverrideCount: dataSharesStore.outgoingOverrideCountLookup,
    getIncomingSharedFieldCount: dataSharesStore.incomingSharedFieldCountLookup,
    partnerActivityLookup: partnerActivityLookup.value,
  });
});

/**
 * Owner options are identical for every row, so they are built once here rather
 * than per cell instance.
 */
const teamOwnerOptions = computed(() => buildOwnerOptions(teamStore.coreUsers));

const SETTINGS_TAB_BY_SIDE: Record<
  SharingSideType,
  PartnershipSettingsTabType
> = {
  incoming: PartnershipSettingsTabType.SHARED_WITH_YOU,
  outgoing: PartnershipSettingsTabType.SHARING_SETTINGS,
};

/**
 * Opened in place rather than through the partnership-settings route, which
 * lives under /partners and would unmount the tab with its filters and scroll.
 */
const partnerSettingsTarget = ref<{
  partnerId: number;
  tab: PartnershipSettingsTabType;
  segment: SegmentType;
} | null>(null);

function openPartnerSettings(
  partnerId: number,
  side: SharingSideType,
  segment: SegmentType,
) {
  partnerSettingsTarget.value = {
    partnerId,
    tab: SETTINGS_TAB_BY_SIDE[side],
    segment,
  };
}

function closePartnerSettings() {
  partnerSettingsTarget.value = null;
}

function editTags(partnerUuid: string) {
  router.push({
    name: 'sharing_settings_edit_partner_tags',
    params: { id: partnerUuid },
  });
}

const ownerPatchVersions = new Map<number, number>();

function findPartner(partnerOrgId: number) {
  return partnersStore.partnerOrgs.find(
    (partner) => partner.id === partnerOrgId,
  );
}

function buildOwnerFromTeam(userId: number | null): Partner['owner'] {
  const authorization = userId
    ? teamStore.coreUsers.find(({ user }) => user.id === userId)
    : undefined;
  if (!authorization) return null;
  const { user } = authorization;
  return {
    user_id: user.id,
    name: [user.first_name, user.last_name].filter(Boolean).join(' ') || null,
  };
}

async function updateRowOwner(partnerOrgId: number, userId: number | null) {
  const target = findPartner(partnerOrgId);
  if (!target) return;

  const version = (ownerPatchVersions.get(partnerOrgId) ?? 0) + 1;
  ownerPatchVersions.set(partnerOrgId, version);
  const isLatest = () => ownerPatchVersions.get(partnerOrgId) === version;

  const previousOwner = target.owner;
  target.owner = buildOwnerFromTeam(userId);

  function rollback() {
    const current = findPartner(partnerOrgId);
    if (current) current.owner = previousOwner;
  }

  try {
    const { error } = await crossbeamApi.PATCH('/v0.1/partners/{uuid}', {
      params: { path: { uuid: target.uuid } },
      body: { owner_user_id: userId },
    });

    if (isLatest() && error) rollback();
  } catch {
    if (isLatest()) rollback();
  }
}

const gridContext = {
  canSelectRows: () => canManagePartners,
  canEditTags: () => canManagePartners,
  canEditOwner: () => canManagePartners,
  editTags,
  openPartnerSettings,
  updateRowOwner,
  teamOwnerOptions: () => teamOwnerOptions.value,
};

const rowSelection = computed<RowSelectionOptions | undefined>(() =>
  canManagePartners
    ? {
        mode: 'multiRow',
        enableClickSelection: false,
        checkboxes: false,
        headerCheckbox: false,
      }
    : undefined,
);

const { filteredRows, applySearch, applyFilters, applyPopulationFilters } =
  usePartnerTabFilters(rows);

const { selectedRows, handleGridReady, clearSelection } =
  usePartnerTabSelection(filteredRows);

/**
 * Built from the rows the table shows, so the dropdown can never offer a
 * partner that buildPartnerRows has dropped.
 */
const partnerOptions = computed(() =>
  rows.value
    .map((row) => ({ label: row.partner.name, value: row.id }))
    .sort((a, b) => a.label.localeCompare(b.label)),
);

const tagOptions = computed(() =>
  partnersStore.partnerTags
    .filter((tag) => tag.label)
    .map((tag) => ({ label: tag.label, value: String(tag.id) }))
    .sort((a, b) => a.label.localeCompare(b.label)),
);

const noRowsProps = computed(() =>
  rows.value.length ? NO_MATCHES_PROPS : NO_PARTNERS_PROPS,
);

const hasAnyChanges = ref(false);
const isSaving = ref(false);

defineExpose({
  hasAnyChanges,
  isSaving,
  selectedRows,
  clearSelection,
  async saveChanges() {
    // No-op: By Partners tab has no editable state yet
  },
});
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.sharing-settings-by-partners-tab {
  @apply flex flex-col w-full;
}

.sharing-settings-by-partners-tab__actions {
  @apply px-24 pb-12;
}

/* --- Edge-to-edge table (like PartnerTrackerPage) --- */
:deep(.ag-root-wrapper) {
  @apply rounded-none! border-x-0 border-b-0;
}

/* --- AG Grid column group borders (mirroring PartnerLeaderboardTable) --- */
:deep(.ag-header-cell) {
  @apply border-0!;
}
:deep(.ag-header-group-cell:not(.ag-header-group-cell-no-group)) {
  @apply border-0! border-r! border-solid! border-neutral-300;
}
:deep(.ag-pinned-left-header .ag-header-group-cell-no-group) {
  @apply border-0! border-r! border-solid! border-neutral-300;
}
:deep(.border-end) {
  @apply border-0! border-r! border-solid! border-neutral-300;
}
:deep(.ag-header-row.ag-header-row-column-group) {
  @apply bg-neutral-background text-xs text-neutral-text-strong font-normal;
}

:deep(.ag-header-group-cell-label) {
  @apply justify-center;
}

:deep(.segment-header .ag-header-cell-label) {
  @apply justify-center;
}

:deep(.ag-header-group-cell.ag-header-group-cell-no-group) {
  @apply bg-neutral-background;
}

/* --- Sharing level cells: 6px apart, and 6px from each column group's edges --- */
:deep(.ag-cell.segment-cell) {
  @apply px-3!;
}
:deep(.ag-cell.segment-cell--first) {
  @apply pl-6!;
}
:deep(.ag-cell.segment-cell--last) {
  @apply pr-6!;
}
:deep(.ag-cell-wrapper) {
  @apply h-full min-w-0;
}

:deep(.ag-row-selected::before) {
  @apply bg-transparent;
}

:deep(.ag-overlay-no-rows-wrapper .c-bitts-empty-state-large-border) {
  @apply border-none;
}
</style>
